import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Eye, Loader2, Tag, Workflow, X } from 'lucide-react'
import Button from '../forms/Button'
import { BENEFIT_FIELDS, RULE_ACTIONS, RULE_OPERATORS } from '../../data/rulesEngineDefaults'
import { useBenefitRuleMasters } from '../../hooks/useBenefitRuleMasters'
import { getRuleSet } from '../../services/rulesEngineService'
import { useToast } from '../../contexts/ToastContext'
import { useTheme } from '../../contexts/ThemeContext'
import type { BenefitRule, RuleAction, RuleSetRecord } from '../../types/rulesEngine'

type RuleSetViewDrawerProps = {
  open: boolean
  ruleSetId: string | null
  fallback?: RuleSetRecord | null
  onClose: () => void
}

const ACTION_ACCENT: Record<RuleAction, { color: string; bg: string; border: string }> = {
  continue: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  approve: { color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },
  deny: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  route_prior_auth: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  flag: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
}

function actionLabel(key: string, masterLabel?: string) {
  if (masterLabel) return masterLabel
  return RULE_ACTIONS.find((item) => item.key === key)?.label ?? key
}

function fieldLabel(rule: BenefitRule, masterLabel?: string) {
  if (masterLabel) return masterLabel
  return BENEFIT_FIELDS.find((item) => item.key === rule.field)?.label ?? rule.field
}

function operatorLabel(rule: BenefitRule, masterLabel?: string) {
  if (masterLabel) return masterLabel
  return RULE_OPERATORS.find((item) => item.key === rule.operator)?.label ?? rule.operator
}

export default function RuleSetViewDrawer({
  open,
  ruleSetId,
  fallback,
  onClose,
}: RuleSetViewDrawerProps) {
  const { theme } = useTheme()
  const { showToast } = useToast()
  const {
    fields: benefitFields,
    operators: benefitOperators,
    actions: benefitActions,
    hasMasters,
  } = useBenefitRuleMasters(open)

  const [record, setRecord] = useState<RuleSetRecord | null>(fallback ?? null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open || !ruleSetId) return
    let cancelled = false
    setRecord(fallback ?? null)
    setIsLoading(true)

    const load = async () => {
      try {
        const fresh = await getRuleSet(ruleSetId)
        if (!cancelled) setRecord(fresh ?? fallback ?? null)
      } catch (error) {
        if (!cancelled) {
          setRecord(fallback ?? null)
          showToast(error instanceof Error ? error.message : 'Unable to load rule set.', {
            type: 'error',
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [open, ruleSetId, fallback, showToast])

  if (!open) return null

  const rules = record?.rules ?? []
  const title = record?.name || fallback?.name || 'View rule set'

  return createPortal(
    <>
      <div
        className="fixed inset-0 top-0 z-[200] bg-slate-900/35 transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed bottom-0 right-0 top-0 z-[201] flex w-full flex-col border-l shadow-[-8px_0_32px_rgba(15,23,42,0.1)] sm:w-3/4 lg:w-2/3"
        style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}
        role="dialog"
        aria-modal="true"
        aria-label="View rule set"
      >
        <div
          className="flex shrink-0 items-center justify-between border-b px-6 py-5 sm:px-8"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
              style={{ backgroundColor: theme.colors.buttonPrimary, color: '#fff' }}
            >
              <Eye className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                View rule set
              </h2>
              <p className="mt-0.5 truncate text-sm" style={{ color: theme.colors.textSecondary }}>
                {title}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors"
            style={{ borderColor: theme.colors.border, color: theme.colors.textSecondary }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          {isLoading && !record ? (
            <div className="flex items-center justify-center py-16" style={{ color: theme.colors.textMuted }}>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading rule set…
            </div>
          ) : !record ? (
            <div className="rounded-2xl border border-dashed py-10 text-center" style={{ borderColor: theme.colors.border }}>
              <Workflow className="mx-auto mb-2 h-8 w-8" style={{ color: theme.colors.textMuted }} />
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                Rule set not found
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div
                className="rounded-xl border p-4"
                style={{ borderColor: theme.colors.cardBorder, backgroundColor: theme.colors.cardBackground }}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold" style={{ color: theme.colors.textPrimary }}>
                      {record.name}
                    </h3>
                    <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: theme.colors.textMuted }}>
                      <Tag className="h-3 w-3" />
                      {record.productServiceSlug || 'No product service slug'}
                    </p>
                  </div>
                  <span
                    className="rounded px-2 py-0.5 text-[11px] font-semibold"
                    style={{
                      color: record.isActive ? '#0d9488' : theme.colors.textMuted,
                      backgroundColor: record.isActive ? '#f0fdfa' : theme.colors.background,
                    }}
                  >
                    {record.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="mt-3 text-xs" style={{ color: theme.colors.textSecondary }}>
                  {rules.length} {rules.length === 1 ? 'rule' : 'rules'} · evaluated top to bottom
                  {isLoading ? ' · Refreshing…' : ''}
                </p>
              </div>

              {rules.length === 0 ? (
                <div
                  className="rounded-2xl border border-dashed py-10 text-center"
                  style={{ borderColor: theme.colors.border }}
                >
                  <Workflow className="mx-auto mb-2 h-8 w-8" style={{ color: theme.colors.textMuted }} />
                  <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                    No rules in this set
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map((rule, index) => {
                    const passAccent = ACTION_ACCENT[rule.action] ?? ACTION_ACCENT.continue
                    const failAccent = ACTION_ACCENT[rule.failAction] ?? ACTION_ACCENT.flag
                    const fieldMaster = hasMasters
                      ? benefitFields.find(
                          (item) => item.id === rule.conditionFieldMasterId || item.value_key === rule.field
                        )
                      : undefined
                    const operatorMaster = hasMasters
                      ? benefitOperators.find(
                          (item) =>
                            item.id === rule.conditionOperatorMasterId || item.value_key === rule.operator
                        )
                      : undefined
                    const passMaster = hasMasters
                      ? benefitActions.find(
                          (item) => item.id === rule.onPassActionMasterId || item.value_key === rule.action
                        )
                      : undefined
                    const failMaster = hasMasters
                      ? benefitActions.find(
                          (item) => item.id === rule.onFailActionMasterId || item.value_key === rule.failAction
                        )
                      : undefined

                    return (
                      <div
                        key={rule.id || `${rule.name}-${index}`}
                        className="rounded-2xl border shadow-sm"
                        style={{
                          borderColor: theme.colors.cardBorder,
                          backgroundColor: theme.colors.cardBackground,
                        }}
                      >
                        <div
                          className="flex items-center gap-2 rounded-t-2xl border-b px-3 py-2.5"
                          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.background }}
                        >
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                            style={{ backgroundColor: theme.colors.buttonPrimary }}
                          >
                            {index + 1}
                          </div>
                          <p className="min-w-0 flex-1 truncate text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>
                            {rule.name || `Rule ${index + 1}`}
                          </p>
                        </div>
                        <div className="space-y-3 p-4">
                          <div
                            className="rounded-xl border p-3"
                            style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.background }}
                          >
                            <div className="mb-2 flex items-center gap-2">
                              <span
                                className="rounded-md px-2 py-0.5 text-[11px] font-bold tracking-wide text-white"
                                style={{ backgroundColor: theme.colors.textSecondary }}
                              >
                                IF
                              </span>
                              <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
                                Condition
                              </span>
                            </div>
                            <p className="text-sm" style={{ color: theme.colors.textPrimary }}>
                              {fieldLabel(rule, fieldMaster?.display_label)}{' '}
                              <span style={{ color: theme.colors.textSecondary }}>
                                {operatorLabel(rule, operatorMaster?.display_label)}
                              </span>{' '}
                              <span className="font-medium">{rule.value || '—'}</span>
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div
                              className="rounded-xl border p-3"
                              style={{ borderColor: passAccent.border, backgroundColor: passAccent.bg }}
                            >
                              <div className="mb-1 text-xs font-bold" style={{ color: passAccent.color }}>
                                ON PASS
                              </div>
                              <p className="text-sm font-medium" style={{ color: passAccent.color }}>
                                {actionLabel(rule.action, passMaster?.display_label)}
                              </p>
                            </div>
                            <div
                              className="rounded-xl border p-3"
                              style={{ borderColor: failAccent.border, backgroundColor: failAccent.bg }}
                            >
                              <div className="mb-1 text-xs font-bold" style={{ color: failAccent.color }}>
                                ON FAIL
                              </div>
                              <p className="text-sm font-medium" style={{ color: failAccent.color }}>
                                {actionLabel(rule.failAction, failMaster?.display_label)}
                              </p>
                            </div>
                          </div>
                          {rule.failMessage ? (
                            <p className="text-xs" style={{ color: theme.colors.textMuted }}>
                              Fail message: {rule.failMessage}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="flex shrink-0 justify-end border-t px-6 py-4 sm:px-8"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.cardBackground }}
        >
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </>,
    document.body
  )
}
